import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { IProduct } from "@evennt-tech/api-v2-core";
import {
  IDeleteProductQueryReq,
  IGetAllFeeProductsBySupplierQueryReq,
  IGetAllFeeProductsBySupplierRes,
  IGetManyProductsBySupplierQueryReq,
  IGetOneProductBySupplierCompanyQueryReq,
  IGetProductFiltersBySupplierQueryReq,
  IProductFilters,
  IProductSupplierCategoryWithProducts,
  IProductSupplierListItem,
  IUpsertProductReqDto,
  IUpsertProductResDto,
} from "@evennt-tech/api-v2-external";
import { apiV2 } from "api/firebase";
import {
  hasMoreCategoryPages,
  withProductPaging,
} from "store/product/grouped-categories";
import {
  matchesProductChange,
  PRODUCT_CATALOGUE_CHANGE_KEYS,
  subscribeToProductChanges,
} from "store/product/product-notifications";
import { registerStore } from "store/utils/reset-store.utils";

type PaginationMeta = {
  page: number;
  limit: number;
  total: number;
};

type State = {
  products: IProductSupplierListItem[];
  productsPagination: PaginationMeta;
  lastUsedParams: IGetManyProductsBySupplierQueryReq | null;
  allProductsTotal: number;
  allProductsTotalParams: AllProductsTotalParams | null;
  groupedProducts: IProductSupplierCategoryWithProducts[];
  groupedProductsPagination: PaginationMeta;
  groupedProductsParams: GroupedProductsBySupplierParams | null;
  groupedProductsLoading: boolean;
  groupedCategoriesLoadingMore: boolean;
  groupedCategoryLoading: Record<string, boolean>;
  groupedCategoryPages: Record<string, number>;
  groupedCategoryLimits: Record<string, number>;
  groupedCategorySearch: Record<string, string>;
  feeProducts: IGetAllFeeProductsBySupplierRes | null;
  feeParams: IGetAllFeeProductsBySupplierQueryReq | null;
  productFilters: IProductFilters | null;
  productFiltersParams: IGetProductFiltersBySupplierQueryReq | null;
  isLoading: boolean;
  // Separate slice for the "recommended products" picker so its pagination
  // doesn't overwrite the supplier-company products list above.
  recommendedProducts: IProductSupplierListItem[];
  recommendedProductsPagination: PaginationMeta;
  recommendedProductsParams: IGetManyProductsBySupplierQueryReq | null;
  recommendedProductsLoading: boolean;
};

type Action = {
  upsert: (body: IUpsertProductReqDto) => Promise<IUpsertProductResDto>;
  delete: (params: IDeleteProductQueryReq) => Promise<void>;
  getManyBySupplier: (
    params: IGetManyProductsBySupplierQueryReq
  ) => Promise<void>;
  getAllProductsTotalBySupplier: (
    params: AllProductsTotalParams
  ) => Promise<void>;
  getOneBySupplier: (
    params: IGetOneProductBySupplierCompanyQueryReq
  ) => Promise<IProduct | null>;
  getManyBySupplierGroupedByCategory: (
    params: GroupedProductsBySupplierParams
  ) => Promise<void>;
  loadMoreGroupedCategories: () => Promise<void>;
  setGroupedCategoryPage: (
    categoryId: string,
    page: number,
    options?: { force?: boolean; limit?: number }
  ) => Promise<void>;
  setGroupedCategorySearch: (
    categoryId: string,
    search: string
  ) => Promise<void>;
  getAllFeeBySupplier: (
    params: IGetAllFeeProductsBySupplierQueryReq
  ) => Promise<void>;
  getProductFiltersBySupplier: (
    params: IGetProductFiltersBySupplierQueryReq
  ) => Promise<void>;
  fetchRecommendedProducts: (
    params: IGetManyProductsBySupplierQueryReq
  ) => Promise<void>;
  loadMoreRecommendedProducts: () => void;
  autoCatchRecommendedProducts: () => void;
  autoCatchBySupplier: () => void;
  autoCatchBySupplierGroupedByCategory: () => void;
  autoCatchFeeBySupplier: () => void;
};

type Store = State & Action;

export type AllProductsTotalParams = {
  supplierCompanyId: string;
};

export type GroupedProductsBySupplierParams = Omit<
  IGetManyProductsBySupplierQueryReq,
  "page" | "limit"
>;

const DEFAULT_PAGINATION: PaginationMeta = { page: 1, limit: 0, total: 0 };
export const PRODUCTS_PER_CATEGORY = 10;

export const useProductStore = create(
  devtools<Store>(
    (set, get) => ({
      products: [],
      productsPagination: DEFAULT_PAGINATION,
      lastUsedParams: null,
      allProductsTotal: 0,
      allProductsTotalParams: null,
      groupedProducts: [],
      groupedProductsPagination: DEFAULT_PAGINATION,
      groupedProductsParams: null,
      groupedProductsLoading: false,
      groupedCategoriesLoadingMore: false,
      groupedCategoryLoading: {},
      groupedCategoryPages: {},
      groupedCategoryLimits: {},
      groupedCategorySearch: {},
      feeProducts: null,
      feeParams: null,
      productFilters: null,
      productFiltersParams: null,
      isLoading: false,
      recommendedProducts: [],
      recommendedProductsPagination: DEFAULT_PAGINATION,
      recommendedProductsParams: null,
      recommendedProductsLoading: false,

      upsert: (body) => apiV2.product.upsert(body),

      delete: async (params) => {
        await apiV2.product.delete(params);
      },

      getOneBySupplier: async (params) => {
        try {
          return await apiV2.product.supplier.getOne(params);
        } catch (e) {
          console.error(e);
          return null;
        }
      },

      getManyBySupplier: async (params) => {
        set({ isLoading: true, lastUsedParams: params });

        try {
          const data = await apiV2.product.supplier.getMany(params);
          set({
            products: data.items,
            productsPagination: {
              page: data.meta.currentPage,
              limit: data.meta.itemsPerPage,
              total: data.meta.totalItems ?? 0,
            },
          });
        } catch (e) {
          console.error(e);
        } finally {
          set({ isLoading: false });
        }
      },

      getAllProductsTotalBySupplier: async (params) => {
        set({ allProductsTotalParams: params });

        try {
          const data = await apiV2.product.supplier.getMany({
            ...params,
            page: 1,
            limit: 1,
          });

          set({ allProductsTotal: data.meta.totalItems ?? 0 });
        } catch (e) {
          console.error(e);
        }
      },

      getManyBySupplierGroupedByCategory: async (params) => {
        set({
          groupedProductsLoading: true,
          groupedProductsParams: params,
        });

        try {
          const data = await apiV2.product.supplier.getManyGroupedByCategory(
            withProductPaging(
              { ...params, page: 1 },
              { productsPage: 1, productsLimit: PRODUCTS_PER_CATEGORY }
            )
          );
          set({
            groupedProducts: data.items,
            groupedProductsPagination: {
              page: 1,
              limit: data.meta.itemsPerPage,
              total: data.meta.totalItems ?? 0,
            },
            groupedCategoryPages: {},
            groupedCategoryLimits: {},
            groupedCategorySearch: {},
          });
        } catch (e) {
          console.error(e);
        } finally {
          set({ groupedProductsLoading: false });
        }
      },

      loadMoreGroupedCategories: async () => {
        const {
          groupedProductsParams,
          groupedProducts,
          groupedProductsPagination,
          groupedProductsLoading,
          groupedCategoriesLoadingMore,
        } = get();

        if (
          !groupedProductsParams ||
          !groupedProducts.length ||
          groupedProductsLoading ||
          groupedCategoriesLoadingMore ||
          !hasMoreCategoryPages(groupedProductsPagination)
        ) {
          return;
        }

        const nextPage = groupedProductsPagination.page + 1;

        set({ groupedCategoriesLoadingMore: true });

        try {
          const data = await apiV2.product.supplier.getManyGroupedByCategory(
            withProductPaging(
              {
                ...groupedProductsParams,
                page: nextPage,
              },
              { productsPage: 1, productsLimit: PRODUCTS_PER_CATEGORY }
            )
          );

          set((state) => {
            const loadedIds = new Set(
              state.groupedProducts.map(({ id }) => id)
            );

            return {
              groupedProducts: [
                ...state.groupedProducts,
                ...data.items.filter(({ id }) => !loadedIds.has(id)),
              ],
              groupedProductsPagination: {
                page: nextPage,
                limit: data.meta.itemsPerPage,
                total:
                  data.meta.totalItems ?? state.groupedProductsPagination.total,
              },
            };
          });
        } catch (e) {
          console.error(e);
        } finally {
          set({ groupedCategoriesLoadingMore: false });
        }
      },

      setGroupedCategoryPage: async (categoryId, page, options) => {
        const {
          groupedProductsParams,
          groupedCategoryLoading,
          groupedCategoryLimits,
          groupedCategorySearch,
        } = get();

        if (!groupedProductsParams) return;
        if (groupedCategoryLoading[categoryId] && !options?.force) return;

        const search = groupedCategorySearch[categoryId] ?? "";
        const limit =
          options?.limit ??
          groupedCategoryLimits[categoryId] ??
          PRODUCTS_PER_CATEGORY;

        set((state) => ({
          groupedCategoryLoading: {
            ...state.groupedCategoryLoading,
            [categoryId]: true,
          },
        }));

        try {
          const data = await apiV2.product.supplier.getMany({
            ...groupedProductsParams,
            categoryIds: categoryId,
            search: search || undefined,
            page,
            limit,
          });

          // A newer term for this category superseded this request while it
          // was in flight — its rows would contradict the input.
          if ((get().groupedCategorySearch[categoryId] ?? "") !== search) {
            return;
          }

          set((state) => ({
            groupedProducts: state.groupedProducts.map((category) =>
              category.id === categoryId
                ? {
                    ...category,
                    products: data.items,
                    totalProducts:
                      data.meta.totalItems ?? category.totalProducts,
                    hasMore: (data.meta.totalItems ?? 0) > page * limit,
                  }
                : category
            ),
            groupedCategoryPages: {
              ...state.groupedCategoryPages,
              [categoryId]: page,
            },
            groupedCategoryLimits: {
              ...state.groupedCategoryLimits,
              [categoryId]: limit,
            },
          }));
        } catch (e) {
          console.error(e);
        } finally {
          set((state) => ({
            groupedCategoryLoading: {
              ...state.groupedCategoryLoading,
              [categoryId]: false,
            },
          }));
        }
      },

      setGroupedCategorySearch: async (categoryId, search) => {
        if ((get().groupedCategorySearch[categoryId] ?? "") === search) return;

        set((state) => ({
          groupedCategorySearch: {
            ...state.groupedCategorySearch,
            [categoryId]: search,
          },
        }));

        await get().setGroupedCategoryPage(categoryId, 1, { force: true });
      },

      getAllFeeBySupplier: async (params) => {
        set({ isLoading: true, feeParams: params });

        try {
          const data = await apiV2.product.getAllFeeBySupplier(params);
          set({ feeProducts: data });
        } catch (e) {
          console.error(e);
        } finally {
          set({ isLoading: false });
        }
      },

      getProductFiltersBySupplier: async (params) => {
        set({ productFiltersParams: params });

        try {
          const productFilters = await apiV2.product.getProductFiltersBySupplier(
            params
          );
          set({ productFilters });
        } catch (e) {
          console.error("Error during loading product filters: ", e);
        }
      },

      fetchRecommendedProducts: async (params) => {
        set({
          recommendedProductsLoading: true,
          recommendedProductsParams: params,
        });

        try {
          const data = await apiV2.product.supplier.getMany(params);
          set((state) => {
            const isLoadMore = (params.page ?? 1) > 1;
            const existingIds = new Set(
              state.recommendedProducts.map((product) => product.id)
            );
            const items = isLoadMore
              ? [
                  ...state.recommendedProducts,
                  ...data.items.filter((item) => !existingIds.has(item.id)),
                ]
              : data.items;

            return {
              recommendedProducts: items,
              recommendedProductsPagination: {
                page: data.meta.currentPage,
                limit: data.meta.itemsPerPage,
                total: data.meta.totalItems ?? 0,
              },
            };
          });
        } catch (e) {
          console.error(e);
        } finally {
          set({ recommendedProductsLoading: false });
        }
      },

      loadMoreRecommendedProducts: () => {
        const {
          recommendedProducts,
          recommendedProductsPagination,
          recommendedProductsParams,
          recommendedProductsLoading,
          fetchRecommendedProducts,
        } = get();

        if (!recommendedProductsParams || recommendedProductsLoading) return;

        const { page, total, limit } = recommendedProductsPagination;
        if (recommendedProducts.length >= total) return;

        fetchRecommendedProducts({
          ...recommendedProductsParams,
          page: page + 1,
          limit,
        });
      },

      autoCatchRecommendedProducts: () => {
        subscribeToProductChanges({
          keys: PRODUCT_CATALOGUE_CHANGE_KEYS,
          listenerIdPrefix: "product_recommended_store",
          callback: (payload) => {
            const { recommendedProductsParams } = get();

            if (!recommendedProductsParams) return;
            if (!matchesProductChange(payload, recommendedProductsParams)) {
              return;
            }

            get().fetchRecommendedProducts({
              ...recommendedProductsParams,
              page: 1,
            });
          },
        });
      },

      autoCatchBySupplier: () => {
        subscribeToProductChanges({
          listenerIdPrefix: "product_supplier_store",
          callback: (payload) => {
            const { lastUsedParams, productFiltersParams } = get();

            if (
              lastUsedParams &&
              matchesProductChange(payload, lastUsedParams)
            ) {
              get().getManyBySupplier(lastUsedParams);
            }

            if (
              productFiltersParams &&
              matchesProductChange(payload, productFiltersParams)
            ) {
              get().getProductFiltersBySupplier(productFiltersParams);
            }
          },
        });
      },

      autoCatchBySupplierGroupedByCategory: () => {
        subscribeToProductChanges({
          listenerIdPrefix: "product_supplier_grouped_store",
          callback: (payload) => {
            const { groupedProductsParams, allProductsTotalParams } = get();

            if (!groupedProductsParams) return;
            if (!matchesProductChange(payload, groupedProductsParams)) return;

            get().getManyBySupplierGroupedByCategory(groupedProductsParams);

            if (allProductsTotalParams) {
              get().getAllProductsTotalBySupplier(allProductsTotalParams);
            }
          },
        });
      },

      autoCatchFeeBySupplier: () => {
        subscribeToProductChanges({
          keys: PRODUCT_CATALOGUE_CHANGE_KEYS,
          listenerIdPrefix: "product_fee_store",
          callback: (payload) => {
            const { feeParams } = get();

            if (!feeParams) return;
            if (!matchesProductChange(payload, feeParams)) return;

            get().getAllFeeBySupplier(feeParams);
          },
        });
      },
    }),
    { name: "product" }
  )
);

registerStore(useProductStore);
